// M12 — BrandingEditor reusable komponenti. PLAN §9.3.
// mode="system" → default brand. mode="client" → per-client override + toggle.

import { useEffect, useState } from "react";
import type { BrandingConfig } from "@mahir/shared";
import { BODY_FONTS, HEADING_FONTS, HARDCODED_DEFAULT_BRANDING } from "@mahir/shared";
import { PERIOD_COPY } from "../../lib/period-ui";

interface Props {
  mode: "system" | "client";
  initial: BrandingConfig | null;
  initialLogoUrl: string | null;
  onSave: (config: BrandingConfig) => Promise<void>;
  onDisable?: () => Promise<void>;
  onLogoUpload: (file: File) => Promise<void>;
  onLogoDelete: () => Promise<void>;
}

const TOKEN_CHIPS = [
  { token: "{client_name}", label: "Müşteri adı" },
  { token: "{framework_name}", label: "Çerçeve" },
  { token: "{year}", label: "Yıl" },
  { token: "{period}", label: PERIOD_COPY.brandingTokenLabel },
  { token: "{date}", label: "Tarih" },
  { token: "{page}", label: "Sayfa no" },
  { token: "{total_pages}", label: "Toplam sayfa" },
];

const COLOR_LABELS: Record<keyof BrandingConfig["colors"], string> = {
  primary: "Ana renk",
  secondary: "İkincil",
  text: "Metin",
  bgAccent: "Vurgu zemin",
  muted: "Soluk",
};

export function BrandingEditor(props: Props): JSX.Element {
  const { mode, initial, initialLogoUrl, onSave, onDisable, onLogoUpload, onLogoDelete } = props;
  const [clientEnabled, setClientEnabled] = useState<boolean>(
    mode === "system" || initial !== null,
  );
  const [config, setConfig] = useState<BrandingConfig>(initial ?? HARDCODED_DEFAULT_BRANDING);
  const [logoUrl, setLogoUrl] = useState<string | null>(initialLogoUrl);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    setConfig(initial ?? HARDCODED_DEFAULT_BRANDING);
    setClientEnabled(mode === "system" || initial !== null);
  }, [initial, mode]);

  useEffect(() => {
    setLogoUrl(initialLogoUrl);
  }, [initialLogoUrl]);

  const handleColorChange =
    (key: keyof BrandingConfig["colors"]) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setConfig((c) => ({ ...c, colors: { ...c.colors, [key]: e.target.value } }));
    };

  const handleFontPick =
    (key: keyof BrandingConfig["fonts"], curated: readonly string[]) =>
    (e: React.ChangeEvent<HTMLSelectElement>): void => {
      const v = e.target.value;
      if (v === "__custom__") {
        // Custom mode: mevcut value zaten custom (curated'da değil) ise koru;
        // curated'dan geçiyorsa boşla → input açık + boş + uyarı görünür.
        const cur = config.fonts[key];
        const alreadyCustom = !curated.includes(cur);
        setConfig((c) => ({ ...c, fonts: { ...c.fonts, [key]: alreadyCustom ? cur : "" } }));
      } else {
        setConfig((c) => ({ ...c, fonts: { ...c.fonts, [key]: v } }));
      }
    };

  const handleFontCustom =
    (key: keyof BrandingConfig["fonts"]) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setConfig((c) => ({ ...c, fonts: { ...c.fonts, [key]: e.target.value } }));
    };

  const handleTemplateChange =
    (key: keyof BrandingConfig["templates"]) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setConfig((c) => ({ ...c, templates: { ...c.templates, [key]: e.target.value } }));
    };

  const insertToken = (key: keyof BrandingConfig["templates"], token: string): void => {
    setConfig((c) => ({
      ...c,
      templates: { ...c.templates, [key]: (c.templates[key] ?? "") + " " + token },
    }));
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await onSave(config);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleClient = async (next: boolean): Promise<void> => {
    setClientEnabled(next);
    if (!next && onDisable) {
      try {
        await onDisable();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Devre dışı bırakılamadı");
      }
    }
  };

  const handleLogoFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    setError(null);
    try {
      await onLogoUpload(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Logo yüklenemedi");
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };

  const handleLogoDelete = async (): Promise<void> => {
    if (!confirm("Logo silinsin mi?")) return;
    try {
      await onLogoDelete();
      setLogoUrl(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Silinemedi");
    }
  };

  const disabled = mode === "client" && !clientEnabled;

  return (
    <div className="flex flex-col gap-5">
      {mode === "client" && (
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input
            type="checkbox"
            checked={clientEnabled}
            onChange={(e) => void handleToggleClient(e.target.checked)}
          />
          Bu müşteri için özel brand kullan
        </label>
      )}

      {error && (
        <div
          className="px-3 py-2 rounded text-sm"
          style={{
            background: "color-mix(in srgb, var(--color-error) 10%, white)",
            color: "var(--color-error)",
          }}
        >
          {error}
        </div>
      )}

      <fieldset
        disabled={disabled}
        className="flex flex-col gap-5"
        style={{ opacity: disabled ? 0.5 : 1 }}
      >
        <section>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "var(--color-text)" }}>
            Logo (PNG/JPG · max 5MB · yatay)
          </h3>
          <div className="flex items-center gap-4">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo"
                style={{
                  maxHeight: 80,
                  maxWidth: 240,
                  border: "1px solid var(--color-border)",
                  borderRadius: 4,
                  padding: 4,
                  background: "white",
                }}
              />
            ) : (
              <div
                className="text-xs"
                style={{
                  width: 240,
                  height: 80,
                  border: "1px dashed var(--color-border)",
                  borderRadius: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--color-text-3)",
                }}
              >
                Logo yok
              </div>
            )}
            <div className="flex flex-col gap-2">
              <label
                className="px-3 py-1.5 rounded text-sm cursor-pointer"
                style={{
                  background: "var(--color-primary)",
                  color: "var(--color-surface)",
                  fontWeight: 600,
                }}
              >
                {uploadingLogo ? "Yükleniyor…" : logoUrl ? "Değiştir" : "Yükle"}
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) => void handleLogoFile(e)}
                />
              </label>
              {logoUrl && (
                <button
                  onClick={() => void handleLogoDelete()}
                  className="px-3 py-1.5 rounded text-sm"
                  style={{
                    background: "transparent",
                    border: "1px solid var(--color-border)",
                  }}
                >
                  Sil
                </button>
              )}
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-sm font-semibold mb-2">Renkler</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {(["primary", "secondary", "text", "bgAccent", "muted"] as const).map((key) => (
              <label key={key} className="flex items-center gap-2">
                <span style={{ minWidth: 90 }}>{COLOR_LABELS[key]}</span>
                <input
                  type="color"
                  value={config.colors[key]}
                  onChange={handleColorChange(key)}
                  style={{ width: 40, height: 32, border: "none", padding: 0, cursor: "pointer" }}
                />
                <code style={{ fontSize: "0.75rem", color: "var(--color-text-3)" }}>
                  {config.colors[key]}
                </code>
              </label>
            ))}
          </div>
        </section>

        <section>
          <h3 className="text-sm font-semibold mb-2">Fontlar</h3>
          <p className="text-xs mb-3" style={{ color: "var(--color-text-3)" }}>
            Listedeki fontlar Word'de garantili yüklüdür. "Diğer (Özel)" seçeneğiyle kendi
            markanızın fontunu (Inter, Roboto vs.) girebilirsiniz; bu font açan kullanıcının
            makinasında yoksa Word otomatik benzer fonta düşer.
          </p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <FontPicker
              label="Başlık fontu"
              curated={HEADING_FONTS}
              value={config.fonts.heading}
              onPick={handleFontPick("heading", HEADING_FONTS)}
              onCustom={handleFontCustom("heading")}
            />
            <FontPicker
              label="Metin fontu"
              curated={BODY_FONTS}
              value={config.fonts.body}
              onPick={handleFontPick("body", BODY_FONTS)}
              onCustom={handleFontCustom("body")}
            />
          </div>
        </section>

        <section>
          <h3 className="text-sm font-semibold mb-2">Header / Footer Template'leri</h3>
          <div className="text-xs mb-2" style={{ color: "var(--color-text-3)" }}>
            Token'lar (tıklayarak ekle):
          </div>
          <div className="flex flex-col gap-3 text-sm">
            {(
              [
                ["headerText", "Header"],
                ["footerLeftText", "Footer (sol)"],
                ["footerRightText", "Footer (sağ)"],
              ] as const
            ).map(([key, label]) => (
              <div key={key} className="flex flex-col gap-1">
                <label className="text-sm font-medium">{label}</label>
                <input
                  type="text"
                  value={config.templates[key]}
                  onChange={handleTemplateChange(key)}
                  className="px-2 py-1.5 rounded"
                  style={{ border: "1px solid var(--color-border)" }}
                />
                <div className="flex flex-wrap gap-1 mt-1">
                  {TOKEN_CHIPS.map((c) => (
                    <button
                      key={c.token}
                      onClick={() => insertToken(key, c.token)}
                      className="px-2 py-0.5 rounded text-xs"
                      style={{
                        border: "1px solid var(--color-border)",
                        background: "var(--color-bg)",
                        fontFamily: "var(--font-mono)",
                      }}
                      title={c.label}
                    >
                      {c.token}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h3 className="text-sm font-semibold mb-2">Bölüm başlıkları</h3>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={config.showSectionCodes}
              onChange={(e) => setConfig((c) => ({ ...c, showSectionCodes: e.target.checked }))}
            />
            DOCX bölüm başlıklarında bölüm kodunu göster
          </label>
          <div className="text-xs mt-1" style={{ color: "var(--color-text-3)" }}>
            Kapalıyken başlık yalnız bölüm adıdır ("Rakamlarla Şirket"); açıkken "SF-RAKAMLAR —
            Rakamlarla Şirket" biçiminde yazılır.
          </div>
          <label className="flex items-center gap-2 text-sm mt-3">
            <input
              type="checkbox"
              checked={config.showStandardRefs}
              onChange={(e) => setConfig((c) => ({ ...c, showStandardRefs: e.target.checked }))}
            />
            Her bölümün sonunda "İlgili standart" dipnotunu göster
          </label>
          <div className="text-xs mt-1" style={{ color: "var(--color-text-3)" }}>
            Standart kodu (örn. GRI 2-7) metnin içinde değil, bölüm sonunda küçük puntolu bir satır
            olarak yazılır.
          </div>
        </section>

        <div className="flex gap-2">
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            className="px-4 py-2 rounded"
            style={{
              background: "var(--color-primary)",
              color: "var(--color-surface)",
              fontWeight: 600,
            }}
          >
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </fieldset>
    </div>
  );
}

interface FontPickerProps {
  label: string;
  curated: readonly string[];
  value: string;
  onPick: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onCustom: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function FontPicker(props: FontPickerProps): JSX.Element {
  const { label, curated, value, onPick, onCustom } = props;
  // Custom mode: value curated listesinde değil. Empty de custom say (Diğer'e
  // tıklayıp henüz yazmamış kullanıcı için input açık kalır).
  const isCustom = !curated.includes(value);
  const selectValue = isCustom ? "__custom__" : value;
  return (
    <label className="flex flex-col gap-1">
      <span>{label}</span>
      <select
        value={selectValue}
        onChange={onPick}
        className="px-2 py-1.5 rounded"
        style={{ border: "1px solid var(--color-border)" }}
      >
        {curated.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
        <option value="__custom__">— Diğer (Özel)…</option>
      </select>
      {isCustom && (
        <>
          <input
            type="text"
            value={value}
            onChange={onCustom}
            placeholder="örn. Inter, Roboto, Montserrat"
            className="px-2 py-1.5 rounded mt-1"
            style={{
              border: "1px solid var(--color-border)",
              fontFamily: "var(--font-mono)",
              fontSize: "0.82rem",
            }}
          />
          <span className="text-xs mt-1" style={{ color: "var(--color-warning, #b6862c)" }}>
            ⚠ Bu font Word'de yüklü değilse otomatik benzer fonta düşer.
          </span>
        </>
      )}
    </label>
  );
}
